import React, { useState } from "react";
import { ArrowRight, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Reveal from "./Reveal";
import api, { formatError } from "../../lib/api";

const SERVICES = [
  "Venture Building",
  "Growth Strategy",
  "Fundraising & Investment",
  "Product Development",
  "Team & Leadership",
  "Operations & Scale",
];

const DETAILS = [
  { icon: Mail, label: "Email", text: "a.azaladze@menelausholding.com", href: "mailto:a.azaladze@menelausholding.com" },
];

export default function Contact() {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", service: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent — I'll be in touch within 24–48 hours.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-lg bg-obsidian border border-white/10 px-4 py-3 text-ink placeholder:text-ink-muted focus:outline-none focus:border-amber/60 focus:ring-1 focus:ring-amber/40 transition-colors duration-300";

  return (
    <section id="contact" data-testid="contact-section" className="section-pad relative z-10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Reveal>
          <div className="overline mb-4">Get In Touch</div>
          <h2 className="font-display font-bold text-4xl sm:text-5xl md:text-6xl leading-none tracking-tight mb-14">
            Let's Build <span className="text-amber">Something.</span>
          </h2>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-14">
          <Reveal>
            <p className="text-ink-secondary text-lg leading-relaxed mb-10">
              Whether you have a fully formed business plan or just the seed of an idea, I'd love to hear about your
              venture. Get in touch to start a conversation.
            </p>
            <div className="space-y-6">
              {DETAILS.map((d) => (
                <a key={d.label} href={d.href} className="group flex items-center gap-4">
                  <div className="h-11 w-11 rounded-lg bg-surface border border-white/10 flex items-center justify-center text-amber group-hover:border-amber/40 transition-colors duration-300">
                    <d.icon size={18} />
                  </div>
                  <div>
                    <div className="text-[0.7rem] uppercase tracking-[0.16em] text-ink-muted">{d.label}</div>
                    <div className="text-ink group-hover:text-amber transition-colors duration-300 break-all">{d.text}</div>
                  </div>
                </a>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            {sent ? (
              <div
                data-testid="contact-success"
                className="rounded-2xl border border-amber/20 bg-surface p-8 sm:p-12 h-full flex flex-col items-center justify-center text-center"
              >
                <CheckCircle2 size={44} className="text-amber mb-5" />
                <h3 className="font-display text-3xl font-medium mb-3">Message Sent</h3>
                <p className="text-ink-secondary">
                  Thank you for reaching out. I'll be in touch within 24–48 hours.
                </p>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={submit} className="rounded-2xl border border-white/[0.07] bg-surface p-6 sm:p-8 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-ink-secondary mb-2">First Name</label>
                    <input data-testid="contact-first-name" required value={form.first_name} onChange={update("first_name")} placeholder="Your first name" className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-sm text-ink-secondary mb-2">Last Name</label>
                    <input data-testid="contact-last-name" required value={form.last_name} onChange={update("last_name")} placeholder="Your last name" className={inputCls} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-ink-secondary mb-2">Email Address</label>
                  <input data-testid="contact-email" required type="email" value={form.email} onChange={update("email")} placeholder="your@email.com" className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm text-ink-secondary mb-2">Project Type</label>
                  <select data-testid="contact-service" value={form.service} onChange={update("service")} className={inputCls}>
                    <option value="">Select a service...</option>
                    {SERVICES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-ink-secondary mb-2">Tell Me About Your Project</label>
                  <textarea data-testid="contact-message" required rows={5} value={form.message} onChange={update("message")} placeholder="Describe your venture, where you are today, and what kind of support you're looking for..." className={inputCls} />
                </div>
                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={sending}
                  className="group w-full inline-flex items-center justify-center gap-2 rounded-lg bg-amber px-6 py-4 font-semibold text-obsidian hover:bg-amber-hover transition-colors duration-300 disabled:opacity-60"
                >
                  {sending ? "Sending..." : "Send Message"}
                  {!sending && <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
}
